import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useChatStore } from "../store/useChatStore";

/* ---------- small helpers ---------- */
const getInitials = (name = "", email = "") => {
  const src = name || email || "";
  const parts = src.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
};

const RoleBadge = ({ role }) => {
  if (!role) return null;
  const lower = String(role).toLowerCase();
  const color =
    lower === "client" ? "#EDE7F6" : lower === "admin" ? "#E3F2FD" : "#E8F5E9";
  const text =
    lower === "client" ? "#5E35B1" : lower === "admin" ? "#1976D2" : "#2E7D32";
  return (
    <span
      style={{
        background: color,
        color: text,
        fontSize: 11,
        fontWeight: 700,
        padding: "2px 6px",
        borderRadius: 999,
        marginLeft: 8,
      }}
    >
      {role}
    </span>
  );
};

const Spinner = () => (
  <span
    style={{
      display: "inline-block",
      width: 16,
      height: 16,
      border: "2px solid rgba(255,255,255,0.6)",
      borderTopColor: "#fff",
      borderRadius: "50%",
      animation: "spin .8s linear infinite",
    }}
  />
);

const AssignUserModal = ({ project, onClose, onAssignSuccess }) => {
  const { users = [], getUsers } = useChatStore();
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  /* ---------- load all users ---------- */
  useEffect(() => {
    getUsers();
  }, [getUsers]);

  /* ---------- preselect assigned ---------- */
  useEffect(() => {
    if (project?.assignedUsers)
      setSelectedUserIds(project.assignedUsers.map((u) => u._id || u));
    else setSelectedUserIds([]);
  }, [project]);

  const toggleUser = (id) =>
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = Array.isArray(users) ? users : [];
    return !q
      ? list
      : list.filter(
          (u) =>
            (u.name || "").toLowerCase().includes(q) ||
            (u.email || "").toLowerCase().includes(q) ||
            (u.role || "").toLowerCase().includes(q)
        );
  }, [users, search]);

  /* ---------- assign API ---------- */
  const handleAssign = async () => {
    try {
      setLoading(true);
      setFeedback(null);

      const BASE_URL =
        process.env.NODE_ENV === "production"
          ? "https://erp-api.sdcllp.co.in"
          : "http://localhost:8080";

      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.put(
        `${BASE_URL}/api/projects/${project._id}/assign-users`,
        { userIds: selectedUserIds },
        { headers }
      );

      setFeedback({ type: "success", message: res.data?.message || "Saved" });
      onAssignSuccess?.();
      setTimeout(onClose, 900);
    } catch (err) {
      console.error("âŒ Assign users failed:", {
        status: err?.response?.status,
        url: err?.config?.url,
        data: err?.response?.data,
        message: err?.message,
      });
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to assign users.";
      setFeedback({ type: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  /* ---------- render ---------- */
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        backdropFilter: "blur(1px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          width: 560,
          maxWidth: "96vw",
          maxHeight: "90vh",
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid #EEE",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
              Assign Users
            </h3>
            <div style={{ marginTop: 4, fontSize: 12, color: "#6b7280" }}>
              Project:{" "}
              <strong>
                {project?.projectName || project?.name || "Untitled"}
              </strong>
            </div>
          </div>
          <button
            aria-label="Close"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 20,
              lineHeight: 1,
              cursor: "pointer",
              color: "#6b7280",
            }}
          >
            Ã—
          </button>
        </div>

        {/* Search */}
        <div
          style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}
        >
          <input
            autoFocus
            placeholder="Search by name, email or roleâ€¦"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px 10px 36px",
              borderRadius: 10,
              border: "1px solid #D1D5DB",
              outline: "none",
              background:
                '#fff url("data:image/svg+xml;utf8,<svg xmlns=\\"http://www.w3.org/2000/svg\\" width=\\"16\\" height=\\"16\\" viewBox=\\"0 0 24 24\\" fill=\\"none\\" stroke=\\"%239CA3AF\\" stroke-width=\\"2\\" stroke-linecap=\\"round\\" stroke-linejoin=\\"round\\"><circle cx=\\"11\\" cy=\\"11\\" r=\\"8\\"></circle><line x1=\\"21\\" y1=\\"21\\" x2=\\"16.65\\" y2=\\"16.65\\"></line></svg>") no-repeat 10px center',
            }}
          />
        </div>

        {/* List */}
        <div style={{ padding: 8, overflowY: "auto", flex: 1 }}>
          {filtered.map((u) => {
            const selected = selectedUserIds.includes(u._id);
            return (
              <label
                key={u._id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: 10,
                  borderRadius: 10,
                  border: "1px solid #E5E7EB",
                  margin: 8,
                  cursor: "pointer",
                  background: selected ? "#F0FDF4" : "#fff",
                }}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleUser(u._id)}
                  style={{ transform: "scale(1.1)" }}
                />
                <span
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: "#F3F4F6",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: 13,
                  }}
                >
                  {getInitials(u.name, u.email)}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {u.name || u.email}
                    </span>
                    <RoleBadge role={u.role} />
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    {u.email}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        {/* Feedback */}
        {feedback && (
          <div
            style={{
              padding: "10px 14px",
              margin: "6px 16px 0",
              borderRadius: 8,
              background: feedback.type === "success" ? "#ECFDF5" : "#FEF2F2",
              color: feedback.type === "success" ? "#065F46" : "#991B1B",
              fontSize: 13,
              border: `1px solid ${
                feedback.type === "success" ? "#A7F3D0" : "#FECACA"
              }`,
            }}
          >
            {feedback.message}
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            padding: 14,
            borderTop: "1px solid #EEE",
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #D1D5DB",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={loading}
            style={{
              padding: "10px 16px",
              borderRadius: 10,
              border: "none",
              background: "#2563EB",
              color: "#fff",
              fontWeight: 800,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              opacity: loading ? 0.85 : 1,
            }}
          >
            {loading ? <Spinner /> : null}
            {loading ? "Savingâ€¦" : "Save"}
          </button>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default AssignUserModal;

